import { scaleLinear } from 'd3-scale';
import { dropRightWhile, dropWhile } from 'lodash-es';

/**
 * Represents a tuple of [vkar, frequency] values
 */
type VkarFreqPair = [number, number];

/**
 * Represents an array of vkar-frequency pairs that define a mapping line
 */
export type VkarToFreqLine = VkarFreqPair[];

/**
 * Represents a frequency range with start and end values
 */
type FrequencyRange = [number, number];

/**
 * Internal accumulator type used in the reduce operation
 */
interface FreqSearchAccumulator {
    itemFreq?: number;
    itemVkar?: number;
    prevFreq: number | null;
    prevVkar: number | null;
}

// JS port of SpectrumConditioner::freq_to_vkar.
// https://github.com/omnidots/shared/blob/718785c2fdcdff5911939ea23aa67432aeb189ae/conditioner/spectrum.py#L359
export function freqToVkar(freq: number, vkarToFreqLine: VkarToFreqLine) {
    // Find the slot in which our frequency lies.
    const { itemFreq, itemVkar, prevFreq, prevVkar } = [...vkarToFreqLine].reduce(
        (accumulator: FreqSearchAccumulator, [currentVkar, currentFreq], _index, array) => {
            accumulator.itemFreq = currentFreq;
            accumulator.itemVkar = currentVkar;

            if (freq < currentFreq) {
                // We found our item, eject early.
                array.splice(1);
                return accumulator;
            }

            accumulator.prevFreq = currentFreq;
            accumulator.prevVkar = currentVkar;
            return accumulator;
        },
        {
            prevFreq: null,
            prevVkar: null,
        }
    );

    // In case we lie outside of the range we flat line
    if (prevFreq == null || (prevFreq === itemFreq && prevVkar === itemVkar)) {
        return itemVkar;
    }

    // Otherwise interpolate our frequency
    return scaleLinear().domain([prevFreq, itemFreq!]).range([prevVkar!, itemVkar!])(freq);
}

export function fitVkarToFreqLineIntoRange(
    vkarToFreqLine: VkarToFreqLine,
    [start, end]: FrequencyRange
): Array<[number, number]> {
    return [
        [freqToVkar(start, vkarToFreqLine), start],
        ...dropRightWhile(
            dropWhile(vkarToFreqLine, ([_vkar, freq]) => freq < start),
            ([_vkar, freq]) => freq > end
        ),
        [freqToVkar(end, vkarToFreqLine), end],
    ] as Array<[number, number]>;
}
